import React, { useMemo, useState } from "react";
import { ISystemMail } from "../../Interfaces/ISystemMail";
import {
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { CustomEditor } from "../CustomEditor";
import {
    cardSx,
    fieldSx,
    secondaryButtonSx,
    makeIconCircleSx,
    GREEN_700,
    GOLD_MUTED,
} from "../../core/pvtaStyles";

interface IProps {
    editSystemMail: ISystemMail;
    setEditSystemMail: Function;
    showHeading?: boolean;
}

// Einheitlicher Abschnittskopf: goldener Icon-Kreis + Titel + dezente Hilfsbeschreibung.
const FormSection: React.FC<{
    icon: React.ReactNode;
    title: string;
    description?: string;
    required?: boolean;
    children: React.ReactNode;
}> = ({ icon, title, description, required, children }) => (
    <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box sx={makeIconCircleSx(38)}>{icon}</Box>
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: 16, md: 17 },
                        color: 'var(--pvta-heading)',
                        lineHeight: 1.25,
                    }}
                >
                    {title}
                    {required && (
                        <Box component="span" sx={{ color: GOLD_MUTED, ml: 0.5 }}>*</Box>
                    )}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ color: 'var(--pvta-muted)', display: 'block', mt: 0.25 }}>
                        {description}
                    </Typography>
                )}
            </Box>
        </Stack>
        <Box sx={{ mt: 2, minWidth: 0 }}>{children}</Box>
    </Box>
);


export const SystemMailEdit:React.FC<IProps> = ({editSystemMail, setEditSystemMail, showHeading = true}) => {
    const [systemMailOrg ] = useState(editSystemMail)
    const pendingAttachments = editSystemMail.new_attachments ?? [];
    const deletedAttachmentIds = editSystemMail.delete_attachment_ids ?? [];

    const setText = (txt: string) => {
        setEditSystemMail({...editSystemMail, body : txt});
    }

    const visibleExistingAttachments = useMemo(
        () => (editSystemMail.attachments ?? []).filter(
            (attachment) => !(editSystemMail.delete_attachment_ids ?? []).includes(attachment.id),
        ),
        [editSystemMail.attachments, editSystemMail.delete_attachment_ids],
    );

    const handleAddAttachments = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        if (files.length === 0) {
            return;
        }

        setEditSystemMail({
            ...editSystemMail,
            new_attachments: [...pendingAttachments, ...files],
        });
        event.target.value = "";
    };

    const handleRemoveExistingAttachment = (attachmentId: number) => {
        setEditSystemMail({
            ...editSystemMail,
            delete_attachment_ids: [...deletedAttachmentIds, attachmentId],
        });
    };

    const handleRemoveNewAttachment = (index: number) => {
        setEditSystemMail({
            ...editSystemMail,
            new_attachments: pendingAttachments.filter((_, currentIndex) => currentIndex !== index),
        });
    };

    const formatFileSize = (size: number): string => {
        if (size < 1024) {
            return `${size} B`;
        }
        if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(1)} KB`;
        }
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatDateTime = (value?: string | null): string => {
        if (!value) {
            return '-';
        }
        return new Intl.DateTimeFormat('de-DE', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(value));
    };

    return(
        <Box sx={{ minWidth: 0 }}>
            {showHeading && (
                <Typography
                    sx={{
                        fontFamily: 'var(--pvta-font-serif, serif)',
                        fontWeight: 700,
                        fontSize: { xs: 22, md: 26 },
                        color: 'var(--pvta-heading)',
                        mb: 3,
                    }}
                >
                    {`${systemMailOrg.title}`}
                </Typography>
            )}

            <Stack spacing={{ xs: 3, md: 4 }} sx={{ minWidth: 0 }}>
                {/* A) Eckdaten */}
                <FormSection
                    icon={<LabelOutlinedIcon fontSize="small" />}
                    title="Eckdaten"
                    description="Interner Titel zur Wiedererkennung sowie der Betreff der versendeten Mail."
                    required
                >
                    <Grid container spacing={2} sx={{ minWidth: 0 }}>
                        <Grid item sm={4} xs={12}>
                            <TextField
                                label="Titel (nur intern)"
                                value={editSystemMail.title}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEditSystemMail({...editSystemMail, title: event.target.value})}
                                error={editSystemMail.title === ""}
                                fullWidth
                                required
                                sx={{ ...fieldSx, minWidth: 0 }}
                            />
                        </Grid>

                        <Grid item sm={8} xs={12}>
                            <TextField
                                label="Betreff"
                                value={editSystemMail.subject}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEditSystemMail({...editSystemMail, subject: event.target.value})}
                                error={editSystemMail.subject === ""}
                                fullWidth
                                required
                                sx={{ ...fieldSx, minWidth: 0 }}
                            />
                        </Grid>
                    </Grid>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Erstellt am ${formatDateTime(editSystemMail.created_at)}`}
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Erstellt von ${editSystemMail.created_by?.displayName ?? '-'}`}
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Bearbeitet am ${formatDateTime(editSystemMail.updated_at ?? editSystemMail.created_at)}`}
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Bearbeitet von ${editSystemMail.updated_by?.displayName ?? editSystemMail.created_by?.displayName ?? '-'}`}
                        />
                    </Stack>
                </FormSection>

                {/* B) Vorlagen-Anhänge */}
                <FormSection
                    icon={<AttachFileOutlinedIcon fontSize="small" />}
                    title="Vorlagen-Anhänge"
                    description="Diese Dateien werden bei jedem Versand dieser Systemnachricht mitgeschickt."
                >
                    <Stack spacing={2} sx={{ minWidth: 0 }}>
                        <Box
                            component="label"
                            sx={{
                                display: 'block',
                                p: { xs: 3, sm: 4 },
                                border: '1.5px dashed',
                                borderColor: 'var(--pvta-border-strong)',
                                borderRadius: 'var(--pvta-radius-md)',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: 'var(--pvta-bg-soft)',
                                transition: 'border-color 200ms ease, background-color 200ms ease',
                                '&:hover': {
                                    borderColor: GREEN_700,
                                    backgroundColor: 'var(--pvta-green-100)',
                                },
                            }}
                        >
                            <input
                                type="file"
                                hidden
                                multiple
                                onChange={handleAddAttachments}
                            />
                            <Stack alignItems="center" spacing={1}>
                                <Box sx={makeIconCircleSx(48)}>
                                    <CloudUploadOutlinedIcon />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: 'var(--pvta-heading)' }}>
                                    Dateien hinzufügen
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>
                                    Klicken, um Dateien auszuwählen
                                </Typography>
                            </Stack>
                        </Box>

                        {visibleExistingAttachments.length === 0 && pendingAttachments.length === 0 && (
                            <Typography variant="body2" sx={{ color: 'var(--pvta-muted)' }}>
                                Noch keine Vorlagen-Anhänge hinterlegt.
                            </Typography>
                        )}

                        {visibleExistingAttachments.map((attachment) => (
                            <Box
                                key={attachment.id}
                                sx={{
                                    ...cardSx,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    px: 1.75,
                                    py: 1.25,
                                    minWidth: 0,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--pvta-heading)' }} noWrap>
                                        {attachment.filename}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                                        <Chip size="small" label={formatFileSize(attachment.size)} />
                                        <Button
                                            size="small"
                                            href={attachment.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            startIcon={<OpenInNewOutlinedIcon fontSize="small" />}
                                            sx={{ ...secondaryButtonSx, minHeight: 32, px: 1.5 }}
                                        >
                                            Öffnen
                                        </Button>
                                    </Stack>
                                </Box>
                                <IconButton
                                    color="error"
                                    onClick={() => handleRemoveExistingAttachment(attachment.id)}
                                    sx={{ flexShrink: 0 }}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}

                        {pendingAttachments.map((file, index) => (
                            <Box
                                key={`${file.name}-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    border: '1px dashed',
                                    borderColor: 'var(--pvta-border-strong)',
                                    borderRadius: 'var(--pvta-radius-md)',
                                    backgroundColor: 'var(--pvta-bg-soft)',
                                    px: 1.75,
                                    py: 1.25,
                                    minWidth: 0,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--pvta-heading)' }} noWrap>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>
                                        Neu, wird beim Speichern hochgeladen
                                    </Typography>
                                </Box>
                                <IconButton
                                    color="error"
                                    onClick={() => handleRemoveNewAttachment(index)}
                                    sx={{ flexShrink: 0 }}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Stack>
                </FormSection>

                {/* C) Inhalt */}
                <FormSection
                    icon={<NotesOutlinedIcon fontSize="small" />}
                    title="Inhalt"
                    description="Der Nachrichtentext der Systemmail. Über „HTML Ansicht“ kann der Quelltext bearbeitet werden."
                >
                    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                        <CustomEditor
                            txt={editSystemMail.body}
                            setTxt={setText}
                        />
                    </Box>
                </FormSection>
            </Stack>
        </Box>
    )
}
